import { useDebounce } from 'ahooks';
import { isDateFilterActive } from '../../functions/date.functions';
import { useContext, useEffect, useState } from 'react';
import { NIL } from 'uuid';
import { tFunnelStage, tFunnelsWithStages } from '../../pages/Pipe/Pipe.types';
import { eAlertType } from '../../providers/ToastProvider/Toast.enum';
import { useToastContext } from '../../providers/ToastProvider/Toast.hook';
import { useWorkspaceContext } from '../../providers/WorkspaceProvider/Workspace.context';
import { tWorkspaceUserInfo } from '../../providers/WorkspaceProvider/Workspace.types';
import { AdvanceSearchModalContext } from './AdvanceSearchModal.context';
import { eFilters } from './AdvanceSearchModal.enum';
import { eDateFilterType } from '../../providers/ModalsProvider/Modals.types';
import { eChannelType } from '../../providers/SocketsProvider/Sockets.enums';
import { useIntegrationsContext } from '../../providers/IntegrationsProvider/Integrations.context';
import { AdvanceSearchModalService } from './AdvanceSearchModal.service';
import {
  tAdvanceSearchFilters,
  tAdvanceSearchLead,
  tAdvanceSearchModalContext,
} from './AdvanceSearchModal.types';
import { buildDateFilterParams } from 'src/functions/date.functions';
import { VITE_TIKTOK_FEATURE } from 'src/constants/envs';

export const useAdvanceSearchModalContext = (): tAdvanceSearchModalContext =>
  useContext(AdvanceSearchModalContext);

export function resolveAdvanceSearchPath(
  search: string,
  hasDateParams: boolean,
): 'search' | 'filter' {
  return search.length > 0 && !hasDateParams ? 'search' : 'filter';
}

export default function useAdvanceSearchModal(): tAdvanceSearchModalContext {
  const { currentWorkspaceId } = useWorkspaceContext();
  const { addAlert } = useToastContext();
  const { hasTiktokIntegration } = useIntegrationsContext();

  const [leads, setLeads] = useState<tAdvanceSearchLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isLastPage, setIsLastPage] = useState(false);
  const [totalLeads, setTotalLeads] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [stateFilters, setStateFilters] = useState<eFilters | null>(null);
  const [assignedUserFilters, setAssignedUserFilters] =
    useState<Partial<tWorkspaceUserInfo> | null>(null);
  const [funnelForFilters, setFunnelForFilters] =
    useState<tFunnelsWithStages | null>(null);
  const [stageForFilters, setStageForFilters] = useState<tFunnelStage | null>(
    null,
  );
  const [channelFilter, setChannelFilter] = useState<eChannelType | null>(null);
  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined]);
  const [dateFilterType, setDateFilterType] = useState<eDateFilterType>(
    eDateFilterType.CREATED_AT,
  );
  const [funnels, setFunnels] = useState<tFunnelsWithStages[] | null>(null);
  const [stages, setStages] = useState<tFunnelStage[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);

  const advanceSearchField = useDebounce(inputValue, { wait: 400 });
  const isActiveFilterOn = stateFilters === eFilters.ACTIVE;
  const activeFilterCount = [
    !!stateFilters,
    !!assignedUserFilters,
    !!funnelForFilters,
    !!stageForFilters,
    !!channelFilter,
    isDateFilterActive(dateRange),
  ].filter(Boolean).length;
  const areFiltersActive =
    !!inputValue.length || activeFilterCount > 0;

  // Clear stale TikTok filter when the gating it depended on flips off.
  // Without this, the radio hides but the persisted state still drives
  // `integration=TIKTOK` query params on next search, silently returning
  // zero leads with no UI indication of which filter is active.
  const isTiktokConnected = hasTiktokIntegration();
  useEffect(() => {
    if (
      channelFilter === eChannelType.TIKTOK &&
      (!VITE_TIKTOK_FEATURE || !isTiktokConnected)
    ) {
      setChannelFilter(null);
    }
  }, [isTiktokConnected, channelFilter]);

  useEffect(() => {
    (async () => {
      try {
        if (!currentWorkspaceId) {
          setLeads([]);
          setTotalLeads(0);
          setIsLastPage(false);

          return;
        }

        // RET-1739: no half-open guard here either. `buildDateFilterParams`
        // resolves a one-sided range to a single day, so the request is always
        // well-formed. Leaving this guard in place while `activeFilterCount`
        // counts a start-only range would show "filtro activo" over an
        // unfiltered list.
        setLoading(true);

        if (!funnels?.length) {
          const funnelsWithStagesResponse =
            await AdvanceSearchModalService.getWorkspaceFunnels(
              currentWorkspaceId,
            );

          setFunnels(funnelsWithStagesResponse.data.pipes);
        }

        const dateParams = buildDateFilterParams(dateRange, dateFilterType);
        const queryParams = {
          paginationMode: 'cursor' as const,
          includeTotal: true,
          ...(advanceSearchField && { query: advanceSearchField }),
          ...(stateFilters && { active: isActiveFilterOn }),
          ...(assignedUserFilters && {
            assignedUserId: assignedUserFilters.authId,
          }),
          ...(funnelForFilters &&
            funnelForFilters.id !== NIL && { funnelID: funnelForFilters.id }),
          ...(funnelForFilters &&
            funnelForFilters.id === NIL && { inbox: true }),
          ...(stageForFilters && { stageID: stageForFilters.id }),
          ...(channelFilter && { integration: channelFilter }),
          ...dateParams,
        };

        const hasDateParams = Object.keys(dateParams).length > 0;
        const path = resolveAdvanceSearchPath(
          advanceSearchField,
          hasDateParams,
        );

        const response = await AdvanceSearchModalService.getLeads(
          currentWorkspaceId,
          queryParams,
          path,
        );

        if (areFiltersActive) saveFiltersInLocalStorage();

        if (typeof response.data.total === 'number') {
          setTotalLeads(response.data.total);
        }
        setIsLastPage(response.data.isLastPage || !response.data.nextCursor);
        setNextCursor(response.data.nextCursor);
        setLeads(response.data.leads);
      } catch (err) {
        addAlert({
          type: eAlertType.ERROR,
          message:
            (err as Error).message ||
            'Error al acceder al servidor para traer la información de tus leads',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [
    advanceSearchField,
    stateFilters,
    assignedUserFilters,
    funnelForFilters,
    stageForFilters,
    channelFilter,
    dateRange[0],
    dateRange[1],
    dateRange[0] || dateRange[1] ? dateFilterType : undefined,
  ]);

  function resetFilters() {
    setInputValue('');
    setStateFilters(null);
    setAssignedUserFilters(null);
    setFunnelForFilters(null);
    setStageForFilters(null);
    setChannelFilter(null);
    setDateRange([undefined, undefined]);
    setDateFilterType(eDateFilterType.CREATED_AT);
  }

  function setLastFilters(filters: tAdvanceSearchFilters) {
    setInputValue(filters.inputValue);
    setStateFilters(filters.stateFilters);
    setAssignedUserFilters(filters.assignedUserFilters);
    setFunnelForFilters(filters.funnelForFilters);
    setStageForFilters(filters.stageForFilters);
    setChannelFilter(filters.channelFilter ?? null);
    setDateRange(filters.dateRange);
    setDateFilterType(filters.dateFilterType ?? eDateFilterType.CREATED_AT);
  }

  function saveFiltersInLocalStorage() {
    localStorage.setItem(
      'advanceSearchFilters',
      JSON.stringify({
        inputValue,
        stateFilters,
        assignedUserFilters,
        funnelForFilters,
        stageForFilters,
        channelFilter,
        dateRange,
        dateFilterType,
      }),
    );
  }

  async function getMoreLeads() {
    try {
      if (!currentWorkspaceId || isLastPage || !nextCursor) {
        return;
      }

      const queryParams = {
        paginationMode: 'cursor' as const,
        cursor: nextCursor,
        ...(advanceSearchField && { query: advanceSearchField }),
        ...(stateFilters && { active: isActiveFilterOn }),
        ...(assignedUserFilters && {
          assignedUserId: assignedUserFilters.authId,
        }),
        ...(funnelForFilters &&
          funnelForFilters.id !== NIL && { funnelID: funnelForFilters.id }),
        ...(funnelForFilters && funnelForFilters.id === NIL && { inbox: true }),
        ...(stageForFilters && { stageID: stageForFilters.id }),
        ...(channelFilter && { integration: channelFilter }),
        ...buildDateFilterParams(dateRange, dateFilterType),
      };

      const response = await AdvanceSearchModalService.getLeads(
        currentWorkspaceId,
        queryParams,
        resolveAdvanceSearchPath(
          advanceSearchField,
          Object.keys(buildDateFilterParams(dateRange, dateFilterType)).length >
            0,
        ),
      );

      setIsLastPage(response.data.isLastPage || !response.data.nextCursor);
      setNextCursor(response.data.nextCursor);
      setLeads((prevLeads) => {
        const known = new Set(prevLeads.map((lead) => lead.id));
        return [
          ...prevLeads,
          ...response.data.leads.filter((lead) => !known.has(lead.id)),
        ];
      });
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al acceder al servidor para traer la información de tus leads',
      });
    }
  }

  async function getStages(funnelId: string) {
    try {
      if (!currentWorkspaceId) return;

      setLoadingStages(true);

      const response = await AdvanceSearchModalService.getFunnelStages(
        currentWorkspaceId,
        funnelId,
      );

      setStages(response.data.stages);
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al acceder al servidor para traer la información de tus etapas',
      });
    } finally {
      setLoadingStages(false);
    }
  }

  return {
    leads,
    loading,
    inputValue,
    isLastPage,
    totalLeads,
    stateFilters,
    assignedUserFilters,
    funnelForFilters,
    stageForFilters,
    channelFilter,
    funnels,
    dateRange,
    dateFilterType,
    stages,
    loadingStages,
    areFiltersActive,
    activeFilterCount,
    setStages,
    setDateRange,
    setDateFilterType,
    setStageForFilters,
    setFunnelForFilters,
    setAssignedUserFilters,
    setStateFilters,
    setChannelFilter,
    setLoading,
    getMoreLeads,
    setInputValue,
    setLeads,
    resetFilters,
    setLastFilters,
    saveFiltersInLocalStorage,
    getStages,
  };
}
